import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { CreateGalleryPresetBody, GalleryPresetDto, UpdateGalleryPresetBody } from '@nusign/picflow-backend-types';
import { firstValueFrom, lastValueFrom } from 'rxjs';
import { StandardGalleryPreset } from '../configs/standard-presets';
import { AppConfigService } from '../providers/config.service';
import { DataLoaderService } from './data-loader.service';
import { StateService } from './state.service';
import { TenantService } from './tenant.service';

@Injectable({
  providedIn: 'root',
})
export class GalleryPresetService {
  private http = inject(HttpClient);
  private config = inject(AppConfigService);
  private loader = inject(DataLoaderService);
  private tenantService = inject(TenantService);
  private state = inject(StateService);

  private async getAllCustomGalleryPresets() {
    return await this.loader.get('gallery_presets', () => this.getGalleryPresetsInternal());
  }

  async getCustomGalleryPresets(): Promise<GalleryPresetDto[]> {
    const resp = await this.getAllCustomGalleryPresets();
    const presets = [
      ...resp.filter(p => !p['type'] || p['type'] === 'custom').sort((a, b) => (a.created_at > b.created_at ? 1 : -1)),
    ];
    return presets;
  }

  async getStandardGalleryPresets(): Promise<(StandardGalleryPreset & { visible: boolean })[]> {
    return this.loader.get('standard_gallery_presets', async () => {
      const standardPresets = await firstValueFrom(
        this.http.get<StandardGalleryPreset[]>(`https://picflow.com/internal-api/standard-presets`)
      );
      return standardPresets.map(p => {
        const visible = (this.state.activeTenant.onboarding_checklist['PRESET_VISIBLE_' + p.id] as boolean) ?? true;
        return { ...p, visible };
      });
    });
  }

  async getDefaultPreset(): Promise<GalleryPresetDto | StandardGalleryPreset> {
    const customPresets = await this.getAllCustomGalleryPresets();
    const standardPresets = await this.getStandardGalleryPresets();
    const presets = [...customPresets, ...standardPresets];
    const id = this.state.activeTenant.onboarding_checklist['DEFAULT_PRESET'];
    if (id) return presets.find(p => p.id === id);
    return customPresets.find(p => p.is_default) || standardPresets.find(p => p.id === 'review');
  }

  async getGalleryPresetsInternal(): Promise<GalleryPresetDto[]> {
    const resp = await lastValueFrom(
      this.http.get<{ data: GalleryPresetDto[] }>(`${this.config.apiEndpoint}/gallery_presets`)
    );
    return resp.data;
  }

  async createPreset(preset: CreateGalleryPresetBody) {
    const resp = await lastValueFrom(
      this.http.post<GalleryPresetDto | StandardGalleryPreset>(`${this.config.apiEndpoint}/gallery_presets`, preset)
    );
    this.loader.remove('gallery_presets');
    return resp;
  }

  async deletePreset(id: string) {
    await lastValueFrom(this.http.delete(`${this.config.apiEndpoint}/gallery_presets/${id}`));
    this.loader.remove('gallery_presets');
  }

  async updatePreset(id: string, preset: UpdateGalleryPresetBody) {
    const standardPresets = await this.getStandardGalleryPresets();
    const standardPreset = standardPresets.find(p => p.id === id);
    if (standardPreset) {
      this.state.activeTenant.onboarding_checklist['PRESET_VISIBLE_' + id] = preset.visible;
      await this.tenantService.updateTenant(this.state.activeTenant, {
        onboarding_checklist: this.state.activeTenant.onboarding_checklist,
      });
      return;
    }

    await lastValueFrom(
      this.http.patch<GalleryPresetDto | StandardGalleryPreset>(
        `${this.config.apiEndpoint}/gallery_presets/${id}`,
        preset
      )
    );
    this.loader.remove('gallery_presets');
  }

  async makeDefault(id: string) {
    this.state.activeTenant.onboarding_checklist['DEFAULT_PRESET'] = id;
    await this.tenantService.updateTenant(this.state.activeTenant, {
      onboarding_checklist: this.state.activeTenant.onboarding_checklist,
    });
  }
}
